import type { IEventsFiltersState } from 'Components/EventsFilters/EventsFilters';
import type { TimeSelection } from 'Components/PeriodSelector/PeriodSelector';
import { safeJsonParse } from '~/lib/utils';

export const LOCAL_STORAGE_KEYS = {
  userToken: 'liftstatus-user-token',
} as const;

export class LiftStatusStorage {
  static storeKeyliftstatusOrganisation = 'lifstatus-organisation';
  static storageKeyStartDate = 'liftstatus-start-date';
  static storeKeyEndDate = 'liftstatus-end-date';
  static storeKeyPeriod = 'liftstatus-period';
  static storeKeyEventFilters = 'liftstatus-event-filters';
  static storeKeyAutoRefresh = 'liftstatus-auto-refresh';

  static set lockedLift(lockedLift: string) {
    localStorage.setItem('locked-lift', lockedLift);
  }

  static removeLockedLift() {
    localStorage.removeItem('locked-lift');
  }

  static get lockedLiftId(): string | null {
    return localStorage.getItem('locked-lift');
  }

  static get organisationId(): number | null {
    const organisationId = localStorage.getItem(
      this.storeKeyliftstatusOrganisation
    );
    return !organisationId || organisationId === 'null'
      ? null
      : parseInt(organisationId, 10);
  }

  static set organisationId(organisationId: number | null) {
    if (organisationId === null || organisationId === undefined) {
      localStorage.setItem(this.storeKeyliftstatusOrganisation, 'null');
      return;
    }
    localStorage.setItem(
      this.storeKeyliftstatusOrganisation,
      `${organisationId}`
    );
  }

  static get userToken(): string | null {
    return localStorage.getItem(LOCAL_STORAGE_KEYS['userToken']);
  }

  static set userToken(token: string) {
    localStorage.setItem(LOCAL_STORAGE_KEYS['userToken'], token);
  }

  static get startDate() {
    const storedDate = localStorage.getItem(this.storageKeyStartDate);
    return storedDate ?? new Date().toString();
  }

  static set startDate(date: string) {
    localStorage.setItem(this.storageKeyStartDate, date);
  }

  static get endDate() {
    const storedDate = localStorage.getItem(this.storeKeyEndDate);
    return storedDate ?? new Date().toString();
  }

  static set endDate(date: string) {
    localStorage.setItem(this.storeKeyEndDate, date);
  }

  static get period(): TimeSelection | null {
    const selection = localStorage.getItem(this.storeKeyPeriod);
    return safeJsonParse<TimeSelection>(selection);
  }

  static set period(selection: TimeSelection | null) {
    if (selection) {
      localStorage.setItem(this.storeKeyPeriod, JSON.stringify(selection));
    }
  }

  static set eventFilters(newEventData: IEventsFiltersState) {
    try {
      localStorage.setItem(
        this.storeKeyEventFilters,
        JSON.stringify(newEventData)
      );
    } catch (error) {
      console.error(
        `[storage] error storing ${this.storeKeyEventFilters} in localStorage`,
        error
      );
    }
  }

  static get eventFilters(): IEventsFiltersState | null {
    const eventFiltersString = localStorage.getItem(this.storeKeyEventFilters);
    return safeJsonParse<IEventsFiltersState>(eventFiltersString);
  }

  static get autoRefresh(): boolean {
    return localStorage.getItem(this.storeKeyAutoRefresh) === 'true';
  }

  static set autoRefresh(enabled: boolean) {
    localStorage.setItem(this.storeKeyAutoRefresh, `${enabled}`);
  }
}
